import {$inject, $type} from "../utils/di-container.js";
import {LoggerService} from "./logger.service.js";
import {debounce} from "../utils/functional-utils.js";
import {projectLimit} from "../utils/math-utils.js";

const $callback = Symbol('$callback');
const $fadeListener = Symbol('$fadeListener');

const documentIsReady = () => document.readyState === "complete" || document.readyState === "loaded" || document.readyState === "interactive";

/**
 * Service responsible for global responsiveness, allowing component to:
 * - handle click-outside type of events
 * - handle scroll-away type of events
 */
export class ResponsivenessService {

  /**
   * @private
   * @type {LoggerService}
   */
  _logger;

  /** @type {_BadgePlatform} */
  platform;

  [$inject](obtain) {
    this._logger = obtain(LoggerService);
  }

  initialize() {
    this._checkPlatform();
    this.onWindowResize(debounce(() => this._checkPlatform(), 250), { continuous: true });
  }

  _checkPlatform() {
    const viewportWidth = window.innerWidth;
    const currentPlatform = this.platform;
    if (viewportWidth <= 640) {
      this.platform = 'mobile';
    } else {
      this.platform = 'desktop';
    }
    if (this.platform !== currentPlatform) {
      this._logger.info('global/platform-detected', `Detected platform: ${this.platform}`, () => ({ newPlatform: this.platform, oldPlatform: currentPlatform  }));
      if (this.onPlatformChange[$callback]) {
        this.onPlatformChange[$callback](this.platform);
      }
    }
  }

  /**
   * @param {function()} callback
   * @param {number} buffer additional time to wait in case document is already loaded, in ms
   */
  onPageLoaded(callback, buffer = 0) {
    const listener = () => {
      window.removeEventListener('load', listener);
      callback();
    };
    if (document.readyState === "complete") {
      setTimeout(listener, buffer);
    } else {
      window.addEventListener('load', listener);
    }
  }

  /**
   * @param {Component|BaseComponent} targetComponent
   * @param {function()} callback
   */
  onClickOutside(targetComponent, callback) {
    targetComponent.base.onclick = event => event.stopPropagation();
    const listener = () => {
      document.removeEventListener('click', listener);
      callback();
    };
    document.addEventListener('click', listener);
  }

  /**
   * @param {Component|BaseComponent} targetComponent
   * @param {function()} callback
   */
  onAnimationFinished(targetComponent, callback) {
    const listener = () => {
      document.removeEventListener('animationend', listener);
      callback();
    };
    targetComponent.base.addEventListener('animationend', listener);
  }

  /**
   * @param {function()} callback
   */
  onScrollAway(callback, yOnly = true) {
    let y0 = window.scrollY;
    const listener = e => {
      const y1 = window.scrollY;
      const dy = y1 - y0;
      document.removeEventListener('scroll', listener);
      if (!yOnly || dy > 0) {
        callback();
      }
    };
    document.addEventListener('scroll', listener);
  }

  /**
   * @param {function()} callback
   * @param {boolean} continuous
   */
  onWindowResize(callback, { continuous = false } = {}) {
    const listener = () => {
      if (!continuous) {
        window.removeEventListener('resize', listener);
      }
      callback();
    };
    window.addEventListener("resize", listener);
  }

  /**
   * @param {function(platform: _BadgePlatform)} callback
   */
  onPlatformChange(callback) {
    this.onPlatformChange[$callback] = callback;
  }

  setFadeOnScroll(element, startScroll, endScroll, startOpacity, endOpacity) {
    const apply = () => {
      const opacity = projectLimit(startScroll, startOpacity, endScroll, endOpacity, window.scrollY);
      if (opacity >= 1 || opacity <= 0) {
        element.style.opacity = `${opacity}`;
        if (opacity < 1) {
          element.style.pointerEvents = 'none';
        } else {
          element.style.pointerEvents = '';
        }
      }
      element.style.transition = "opacity 0.2s linear 0s";
    };
    element[$fadeListener] = apply;
    window.addEventListener("scroll", apply);
    apply();
  }

  unsetFadeOnScroll(element) {
    if (element[$fadeListener]) {
      window.removeEventListener('scroll', element[$fadeListener]);
    }
    element.style.opacity = '';
    element.style.pointerEvents = '';
    element.style.transition = "opacity 0.2s linear 0s";
  }

}

/**
 * @returns {Promise<void>} resolved when DOMContentLoaded has been emitted, or the document state suggests is has already been emitted.
 */
ResponsivenessService.whenPageLoaded = async () => {
  if (documentIsReady()) {
    // already loaded
    return;
  } else {
    return new Promise(resolve => {
      const interval = setInterval(() => {
        if (documentIsReady()) {
          clearInterval(interval);
          resolve();
        }
      }, 1000);
      const listener = () => {
        clearInterval(interval);
        resolve();
      };
      window.addEventListener('DOMContentLoaded', listener);
    });
  }
};

ResponsivenessService.isBrowserCompatible = () => {
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf("MSIE"); //IE versions up to 10
  const trident = ua.indexOf("Trident"); //IE11

  return msie <= 0 && trident <= 0;
};


ResponsivenessService[$type] = Symbol('ResponsivenessService');
